import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Card, List, Space, Popconfirm, message, Typography, Breadcrumb, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined, ArrowLeftOutlined, ReloadOutlined, FolderOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Title, Text } = Typography;

interface Hotel {
  id: number;
  name: string;
  pmsType: 'mews' | 'guestline' | 'opera_cloud';
  pmsCredentials: Record<string, string>;
  createdAt: string;
}

interface HotelManagementProps {
  groupId: number;
  groupName: string;
  onBack: () => void;
  onSelectHotel: (hotelId: number, hotelName: string) => void;
  onGroupNameLoaded?: (name: string) => void;
}

const HotelManagement: React.FC<HotelManagementProps> = ({
  groupId,
  groupName,
  onBack,
  onSelectHotel,
  onGroupNameLoaded,
}) => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedGroupName, setLoadedGroupName] = useState(groupName);
  const [emHotels, setEmHotels] = useState<{ id: number; name: string }[]>([]);
  const [form] = Form.useForm();
  const pmsTypeValue = Form.useWatch('pmsType', form);
  const authTypeValue = Form.useWatch('authType', form);

  useEffect(() => {
    loadHotels();
    loadGroupName();
  }, [groupId]);

  const loadGroupName = async () => {
    if (!groupName) {
      try {
        const response = await axios.get(`${API_URL}/hotel-groups/${groupId}`);
        setLoadedGroupName(response.data.name);
        onGroupNameLoaded?.(response.data.name);
      } catch (error) {
        console.error('Failed to load group name');
      }
    }
  };

  const loadHotels = async () => {
    try {
      const response = await axios.get(`${API_URL}/hotels?hotelGroupId=${groupId}`);
      setHotels(response.data);
    } catch (error: any) {
      message.error('Failed to load hotels');
    }
  };

  const loadEmHotels = async (pmsType?: string) => {
    try {
      const url = pmsType ? `${API_URL}/em-hotels?pmsType=${pmsType}` : `${API_URL}/em-hotels`;
      const response = await axios.get(url);
      setEmHotels(response.data);
    } catch (error: any) {
      console.error('Failed to load EM hotels');
    }
  };

  const handleCreate = () => {
    setEditingHotel(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (hotel: Hotel) => {
    setEditingHotel(hotel);
    form.setFieldsValue({
      name: hotel.name,
      pmsType: hotel.pmsType || 'mews',
      ...hotel.pmsCredentials,
    });
    loadEmHotels(hotel.pmsType || 'mews');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/hotels/${id}`);
      message.success('Hotel deleted successfully');
      loadHotels();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete hotel');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const { name, pmsType, emHotelId, ...credFields } = values;
    const pmsCredentials: Record<string, string> = {};
    const mewsFields = ['clientToken', 'accessToken'];
    const guestlineFields = ['siteId', 'interfaceId', 'operatorCode', 'password'];
    const operaCloudFields = ['hostName', 'hotelId', 'appKey', 'clientId', 'clientSecret', 'apiKey', 'authType', 'enterpriseId', 'username', 'password'];
    const credFieldNames = pmsType === 'mews' ? mewsFields : pmsType === 'opera_cloud' ? operaCloudFields : guestlineFields;
    credFieldNames.forEach(f => { if (credFields[f]) pmsCredentials[f] = credFields[f]; });

    try {
      if (editingHotel) {
        await axios.put(`${API_URL}/hotels/${editingHotel.id}`, { name, pmsType, pmsCredentials, emHotelId });
        message.success('Hotel updated successfully');
      } else {
        await axios.post(`${API_URL}/hotels`, { name, pmsType, pmsCredentials, emHotelId, hotelGroupId: groupId });
        message.success('Hotel created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      loadHotels();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchAndSave = async (hotelId: number, hotelName: string) => {
    const hide = message.loading('Syncing PMS data...');
    try {
      const response = await axios.post(`${API_URL}/pms/sync`, { hotelId });

      if (response.data.success) {
        hide();
        message.success(`Data fetched and saved successfully for ${hotelName}!`);
        Modal.success({
          title: 'Data Fetched Successfully',
          content: (
            <div>
              <p>Summary:</p>
              <ul>
                <li>Room Types: {response.data.summary.roomTypes}</li>
                <li>Rooms: {response.data.summary.rooms}</li>
                <li>Features: {response.data.summary.features}</li>
                <li>Products: {response.data.summary.products}</li>
                <li>Images: {response.data.summary.images}</li>
              </ul>
            </div>
          ),
        });
      }
    } catch (error: any) {
      hide();
      message.error(error.response?.data?.message || 'Failed to fetch data');
    }
  };

  return (
    <div style={{ padding: '32px 48px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ marginBottom: 20 }}
        >
          Back to Hotel Groups
        </Button>

        <Breadcrumb
          items={[
            {
              title: (
                <a onClick={onBack} style={{ cursor: 'pointer' }}>
                  <HomeOutlined style={{ marginRight: 4 }} />
                  Hotel Groups
                </a>
              ),
            },
            { title: loadedGroupName || groupName },
          ]}
          style={{ marginBottom: 24, fontSize: 13 }}
        />

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
        }}>
          <div>
            <Title level={2} style={{ margin: 0, marginBottom: 8 }}>{loadedGroupName || groupName}</Title>
            <Text type="secondary" style={{ fontSize: 15 }}>Manage hotels in this group</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="large"
          >
            Add Hotel
          </Button>
        </div>
      </div>

      {hotels.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <HomeOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4}>No hotels yet</Title>
            <Text type="secondary">Add your first hotel to this group</Text>
            <br />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              style={{ marginTop: 16 }}
            >
              Add Hotel
            </Button>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          dataSource={hotels}
          renderItem={(hotel) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => onSelectHotel(hotel.id, hotel.name)}
                style={{ height: '100%' }}
                actions={[
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFetchAndSave(hotel.id, hotel.name);
                    }}
                  >
                    Refresh
                  </Button>,
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(hotel);
                    }}
                  >
                    Edit
                  </Button>,
                  <Popconfirm
                    title="Delete hotel?"
                    description="This will delete all data for this hotel"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDelete(hotel.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  avatar={<HomeOutlined style={{ fontSize: 32, color: '#667eea' }} />}
                  title={hotel.name}
                  description={
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Click to view data
                      </Text>
                    </div>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title={editingHotel ? 'Edit Hotel' : 'Add Hotel'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        width={600}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Hotel Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a hotel name' }]}
          >
            <Input placeholder="e.g., Grand Hotel Amsterdam" />
          </Form.Item>

          <Form.Item
            label="PMS Type"
            name="pmsType"
            rules={[{ required: true, message: 'Please select a PMS type' }]}
          >
            <Select
              options={[{ value: 'mews', label: 'Mews' }, { value: 'guestline', label: 'Guestline' }, { value: 'opera_cloud', label: 'Opera Cloud' }]}
              onChange={(value) => {
                loadEmHotels(value);
                form.setFieldValue('emHotelId', undefined);
              }}
            />
          </Form.Item>

          {pmsTypeValue === 'mews' && (
            <>
              <Form.Item label="Client Token" name="clientToken" initialValue="BF71B49EB4FB4BDD92F6AED600D52DC1-4903D87C662B352F78A97E23E95184E">
                <Input readOnly />
              </Form.Item>
              <Form.Item label="Access Token" name="accessToken" rules={[{ required: true, message: 'Please enter the Access Token' }]}>
                <Input.Password placeholder="Enter your Mews Access Token" />
              </Form.Item>
            </>
          )}

          {pmsTypeValue === 'guestline' && (
            <>
              <Form.Item label="Site ID" name="siteId" rules={[{ required: true, message: 'Please enter the Site ID' }]}>
                <Input placeholder="Enter Guestline Site ID" />
              </Form.Item>
              <Form.Item label="Interface ID" name="interfaceId" rules={[{ required: true, message: 'Please enter the Interface ID' }]}>
                <Input placeholder="Enter Guestline Interface ID" />
              </Form.Item>
              <Form.Item label="Operator Code" name="operatorCode" rules={[{ required: true, message: 'Please enter the Operator Code' }]}>
                <Input placeholder="Enter Guestline Operator Code" />
              </Form.Item>
              <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Please enter the Password' }]}>
                <Input.Password placeholder="Enter Guestline Password" />
              </Form.Item>
            </>
          )}

          {pmsTypeValue === 'opera_cloud' && (
            <>
              <Form.Item label="Host Name" name="hostName" rules={[{ required: true, message: 'Please enter the host URL' }]}>
                <Input placeholder="https://<gateway-host>" />
              </Form.Item>
              <Form.Item label="Hotel ID" name="hotelId" rules={[{ required: true, message: 'Please enter the Hotel ID' }]}>
                <Input placeholder="Opera Cloud hotel code" />
              </Form.Item>
              <Form.Item label="App Key" name="appKey" rules={[{ required: true, message: 'Please enter the App Key' }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item label="Client ID" name="clientId" rules={[{ required: true, message: 'Please enter the Client ID' }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Client Secret" name="clientSecret" rules={[{ required: true, message: 'Please enter the Client Secret' }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item label="API Key (optional)" name="apiKey">
                <Input.Password />
              </Form.Item>
              <Form.Item label="Auth Type" name="authType" initialValue="OCIM">
                <Select options={[{ value: 'OCIM', label: 'OCIM (client credentials)' }, { value: 'password', label: 'Username / Password' }]} />
              </Form.Item>
              {authTypeValue !== 'password' ? (
                <Form.Item label="Enterprise ID" name="enterpriseId" rules={[{ required: true, message: 'Please enter the Enterprise ID' }]}>
                  <Input />
                </Form.Item>
              ) : (
                <>
                  <Form.Item label="Username" name="username" rules={[{ required: true, message: 'Please enter the username' }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Please enter the password' }]}>
                    <Input.Password />
                  </Form.Item>
                </>
              )}
            </>
          )}

          {pmsTypeValue && (
            <Form.Item label="EM Hotel" name="emHotelId">
              <Select
                placeholder="Select EM Hotel (Optional)"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={emHotels.map(hotel => ({
                  value: hotel.id,
                  label: hotel.name,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {editingHotel ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => {
                setIsModalOpen(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HotelManagement;
